import type { SpaceActions } from '@/features/publicsite/types/community/SpaceActions';
import type { SpaceMember } from '@/features/publicsite/types/community/SpaceMember';

const NONE: SpaceActions = { invite: false, removeMembers: false, leave: false };

/**
 * Whether the signed-in account owns the space. The forum itself does not say who owns it; the
 * roster does, as `owner: true` on one row, and that row is compared with the session's account.
 */
export function isSpaceOwner(
  members: Pick<SpaceMember, 'userId' | 'owner'>[],
  userId: string | null,
) {
  return !!userId && members.some((m) => m.owner && m.userId === userId);
}

/**
 * What the reader may do to the space's membership. The owner invites and removes others, and
 * cannot leave (the server refuses it, 422); any other member may leave; a reader who is not on the
 * roster — a moderator reading along, or no session — does none of it.
 */
export function spaceActions(
  members: Pick<SpaceMember, 'userId' | 'owner'>[],
  userId: string | null,
): SpaceActions {
  if (!userId) return NONE;
  if (isSpaceOwner(members, userId)) return { invite: true, removeMembers: true, leave: false };
  if (members.some((m) => m.userId === userId)) return { ...NONE, leave: true };
  return NONE;
}

/** Whether a member's row offers "Scoate": to the owner, on every row but their own. */
export function removableMember(
  member: Pick<SpaceMember, 'userId' | 'owner'>,
  actions: SpaceActions,
  userId: string | null,
): boolean {
  return actions.removeMembers && !member.owner && member.userId !== userId;
}
